// Frontend/src/api/units.ts
import api from "./client";

export type UnitStatus = "occupied" | "vacant" | "inactive";

export type Unit = {
  id: string;
  unit_code: string;
  building_id: string | null;     // FK to Building (UUID)
  building_name: string | null;   // Building name (from FK)
  building_code: string | null;   // Building code (from FK)
  building: string | null;         // Legacy text field (deprecated)
  floor: string | null;
  number: string | null;
  monthly_fee: string; // decimal as string from backend
  alicuota: string; // proportional fee percentage (decimal string)
  initial_balance: string;
  current_balance: string;
  status: UnitStatus;
  status_display: string;
  created_at: string;
  updated_at: string;
};

export type UnitList = {
  id: string;
  unit_code: string;
  building_id: string | null;     // FK to Building (UUID)
  building_name: string | null;   // Building name (from FK)
  building_code: string | null;   // Building code (from FK)
  floor: string | null;
  number: string | null;
  monthly_fee: string;
  alicuota: string; // proportional fee percentage (decimal string)
  current_balance: string;
  status: UnitStatus;
  status_display: string;
  resident_count: number;
};

export type CreateUnitData = {
  unit_code: string;
  building: string;      // FK to Building (UUID) - required
  floor?: string;
  number?: string;
  monthly_fee: number;
  alicuota?: number;     // proportional fee percentage (0–100)
  initial_balance?: number;
  status: UnitStatus;
};

export type UpdateUnitData = Partial<CreateUnitData>;

export type UnitCSVImportResult = {
  success: boolean;
  total_processed: number;
  success_count: number;
  error_count: number;
  errors: Array<{
    row: number;
    field: string;
    message: string;
    data: Record<string, any>;
  }>;
  created_unit_ids: string[];
};

/**
 * List all units, optionally filtered by building
 */
export async function listUnits(buildingId?: string): Promise<UnitList[]> {
  const params = buildingId ? { building: buildingId } : {};
  const res = await api.get<UnitList[]>("/building/units/", { params });
  return res.data;
}

/**
 * List units for a specific building
 */
export async function listUnitsByBuilding(buildingId: string): Promise<UnitList[]> {
  const res = await api.get<UnitList[]>("/building/units/", { 
    params: { building: buildingId } 
  });
  return res.data;
}

/**
 * Get a single unit by ID
 */
export async function getUnit(id: string): Promise<Unit> {
  const res = await api.get<Unit>(`/building/units/${id}/`);
  return res.data;
}

/**
 * Create a new unit
 */
export async function createUnit(data: CreateUnitData): Promise<Unit> {
  const res = await api.post<Unit>("/building/units/", data);
  return res.data;
}

/**
 * Update an existing unit
 */
export async function updateUnit(id: string, data: UpdateUnitData): Promise<Unit> {
  const res = await api.patch<Unit>(`/building/units/${id}/`, data);
  return res.data;
}

/**
 * Delete a unit
 */
export async function deleteUnit(id: string): Promise<void> {
  await api.delete(`/building/units/${id}/`);
}

/**
 * Download CSV template for unit import
 */
export async function downloadUnitCSVTemplate(): Promise<string> {
  const res = await api.get<string>("/building/units/csv-template/");
  return res.data;
}

/**
 * Import units from CSV file
 */
export async function importUnitsCSV(file: File): Promise<UnitCSVImportResult> {
  const formData = new FormData();
  formData.append("file", file);

  const res = await api.post<UnitCSVImportResult>("/building/units/import-csv/", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return res.data;
}
